import React, { useRef } from "react";
import cx from "classnames";

import styles from "./GptAd.module.scss";

/**
 * Props the page or project developer
 * may modify from ad to ad.
 */
export interface GptAdProps {
  className?: string;
  defer?: boolean;
  lazyLoadScreens?: number;

  // k/v targeting props
  targetingPos?: string;
  targetingNative?: string;
}

/**
 * Props required to render the ad, but should
 * be strictly standardized by each type of ad
 * component
 */
interface DetailedGptAdProps extends GptAdProps {
  format:
    | "adhesion"
    | "cineflex"
    | "injector"
    | "logo"
    | "nativevideo"
    | "native"
    | "promo"
    | "recirc"
    | "rrail"
    | "video";

  // Sizes described for all permitted breakpoints
  sizesAt0: string; // Mobile and up, always required
}

interface NoBreakpoint extends DetailedGptAdProps {
  sizesAt976?: undefined;
  sizesAt1010?: undefined;
}

interface LegacyBreakpoint extends DetailedGptAdProps {
  sizesAt1010: string; // Legacy desktop breakpoint
  sizesAt976?: undefined;
}

interface ModernBreakpoint extends DetailedGptAdProps {
  sizesAt976: string; // Goose era desktop breakpoint
  sizesAt1010?: undefined;
}

// Allows passing at most one supported desktop breakpoint
type FinalGptAdProps = NoBreakpoint | LegacyBreakpoint | ModernBreakpoint;

/**
 * We need to memoize this to prevent unnecessary re-renders
 * https://github.com/theatlantic/ads.js#gpt-ad-element
 */
const GptAd = React.memo(
  ({
    className,
    defer = false,
    lazyLoadScreens,
    targetingNative,
    targetingPos,
    format = "injector",
    sizesAt0,
    ...breakpointProps
  }: FinalGptAdProps) => {
    const attrs: {
      [key: string]: string;
    } = {};

    if (defer) {
      attrs["defer"] = "1";
    }

    const ref = useRef<HTMLDivElement>();

    // For each allowed breakpoint, check to see if it exists and
    // if so add it to our elements attributes in the correct format.
    if ((breakpointProps as { sizesAt1010?: string }).sizesAt1010) {
      attrs["sizes-at-1010"] = breakpointProps.sizesAt1010 as string;
    }

    if ((breakpointProps as { sizesAt976?: string }).sizesAt976) {
      attrs["sizes-at-976"] = breakpointProps.sizesAt976 as string;
    }

    return (
      <gpt-ad
        ref={ref}
        // Issue discussing web components recommends using class
        // on custom elements rather than className
        // https://github.com/facebook/react/issues/4933#issuecomment-256664482
        class={cx(styles.root, className)}
        lazy-load={lazyLoadScreens}
        format={format}
        sizes-at-0={sizesAt0}
        // Any property with an `undefined` value will not be on the element
        targeting-pos={targetingPos}
        targeting-native={targetingNative}
        {...attrs}
      ></gpt-ad>
    );
  }
);

GptAd.displayName = "GptAd";

export default GptAd;
